import React, { createContext, useContext, useMemo, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from 'react-query';
import { SnackbarProvider } from 'notistack';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import App from './App';
import './styles.css';

function buildTheme(mode: 'light' | 'dark') {
  const isLight = mode === 'light';
  return createTheme({
    palette: {
      mode,
      // Option B – Calm & Academic (light)
      // Option C – Dark Mode Friendly (dark)
      primary: { main: isLight ? '#4A90E2' : '#60A5FA' },
      secondary: { main: isLight ? '#2D3748' : '#4FD1C5' },
      success: { main: isLight ? '#6BCB77' : '#4FD1C5' },
      background: isLight
        ? { default: '#ffffff', paper: '#ffffff' }
        : { default: '#1E1E2F', paper: '#27293D' }, // Deep navy + lighter panel
      text: isLight
        ? { primary: '#2D3748', secondary: '#4A5568' }
        : { primary: '#E2E8F0', secondary: '#A0AEC0' },
    },
    shape: { borderRadius: 12 },
    typography: {
      fontFamily: 'Inter, Roboto, Arial, sans-serif',
      h5: { fontWeight: 700 },
      h6: { fontWeight: 700 },
      button: { textTransform: 'none', fontWeight: 600 },
    },
    components: {
      MuiButton: {
        defaultProps: { size: 'medium', variant: 'contained' },
        styleOverrides: { root: { borderRadius: 8 } },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            boxShadow: isLight ? '0 6px 20px rgba(0,0,0,0.06)' : '0 8px 24px rgba(0,0,0,0.45)'
          },
        },
      },
      MuiAvatar: {
        styleOverrides: { root: { boxShadow: isLight ? '0 2px 8px rgba(0,0,0,0.12)' : '0 2px 8px rgba(0,0,0,0.4)' } },
      },
    },
  });
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

// Color mode context to toggle theme from anywhere
export const ColorModeContext = createContext<{ mode: 'light' | 'dark'; toggle: () => void }>({ mode: 'light', toggle: () => {} });

export function useColorMode() { return useContext(ColorModeContext); }

function RootApp() {
  // Default to the requested dark palette if no saved preference
  const [mode, setMode] = useState<'light' | 'dark'>(() => {
    const saved = typeof window !== 'undefined' ? localStorage.getItem('app-theme') as 'light' | 'dark' | null : null;
    return saved ?? 'dark';
  });
  const theme = useMemo(() => buildTheme(mode), [mode]);
  const colorMode = useMemo(() => ({
    mode,
    toggle: () => setMode(prev => {
      const next = prev === 'light' ? 'dark' : 'light';
      if (typeof window !== 'undefined') localStorage.setItem('app-theme', next);
      return next;
    })
  }), [mode]);

  return (
    <QueryClientProvider client={queryClient}>
      <ColorModeContext.Provider value={colorMode}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <SnackbarProvider maxSnack={3}>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </SnackbarProvider>
        </ThemeProvider>
      </ColorModeContext.Provider>
    </QueryClientProvider>
  );
}

root.render(
  <React.StrictMode>
    <RootApp />
  </React.StrictMode>
);